import { useCallback, useEffect, useState, type FormEvent } from "react";
import { isAxiosError } from "axios";
import {
  createUserRequest,
  listUsersRequest,
  updateUserRequest,
  type AdminUser,
} from "../../api/adminApi";
import type { Role } from "../../types/domain.types";
import { Spinner } from "../UI/Spinner";

function extractErr(e: unknown, fallback: string): string {
  if (isAxiosError(e)) {
    return (
      (e.response?.data as { error?: { message?: string } } | undefined)?.error
        ?.message ?? fallback
    );
  }
  return fallback;
}

const ROLES: Role[] = ["ADMIN", "TECHNICIAN", "VIEWER"];

export function UserManager() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState<Role>("VIEWER");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUsers(await listUsersRequest());
    } catch (e) {
      setError(extractErr(e, "Error al cargar usuarios"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setCreateError(null);
    if (!newEmail || newPassword.length < 8) {
      setCreateError("Email válido y contraseña de al menos 8 caracteres");
      return;
    }
    setCreating(true);
    try {
      await createUserRequest({
        email: newEmail.trim().toLowerCase(),
        password: newPassword,
        role: newRole,
      });
      setNewEmail("");
      setNewPassword("");
      setNewRole("VIEWER");
      await load();
    } catch (err) {
      setCreateError(extractErr(err, "No se pudo crear el usuario"));
    } finally {
      setCreating(false);
    }
  }

  async function handleToggleActive(u: AdminUser) {
    try {
      await updateUserRequest(u.id, { isActive: !u.isActive });
      setUsers((list) =>
        list.map((x) =>
          x.id === u.id ? { ...x, isActive: !u.isActive } : x,
        ),
      );
    } catch (e) {
      setError(extractErr(e, "No se pudo actualizar el usuario"));
    }
  }

  async function handleChangeRole(u: AdminUser, role: Role) {
    if (role === u.role) return;
    try {
      await updateUserRequest(u.id, { role });
      setUsers((list) =>
        list.map((x) => (x.id === u.id ? { ...x, role } : x)),
      );
    } catch (e) {
      setError(extractErr(e, "No se pudo cambiar el rol"));
    }
  }

  return (
    <section className="flex flex-col gap-4 rounded-md bg-slate-900/50 p-4 ring-1 ring-slate-800">
      <header className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-100">Usuarios</h2>
        <button
          onClick={() => void load()}
          className="text-xs text-slate-400 hover:underline"
        >
          Refrescar
        </button>
      </header>

      {/* Crear usuario */}
      <form
        onSubmit={handleCreate}
        className="grid grid-cols-1 gap-2 rounded-md bg-slate-800/50 p-3 sm:grid-cols-[1fr_1fr_140px_auto]"
      >
        <input
          type="email"
          required
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="email@empresa.com"
          className="rounded-md bg-slate-800 px-3 py-2 text-sm text-slate-100 ring-1 ring-slate-700"
        />
        <input
          type="password"
          required
          minLength={8}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="Contraseña (≥8)"
          autoComplete="new-password"
          className="rounded-md bg-slate-800 px-3 py-2 text-sm text-slate-100 ring-1 ring-slate-700"
        />
        <select
          value={newRole}
          onChange={(e) => setNewRole(e.target.value as Role)}
          className="rounded-md bg-slate-800 px-3 py-2 text-sm text-slate-100 ring-1 ring-slate-700"
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <button
          type="submit"
          disabled={creating}
          className="flex items-center justify-center gap-2 rounded-md bg-brand-light px-3 py-2 text-xs font-semibold text-white hover:bg-brand disabled:opacity-50 sm:col-span-1"
        >
          {creating && <Spinner size={12} />}
          Crear
        </button>
        {createError && (
          <p className="sm:col-span-4 text-xs text-red-400" role="alert">
            {createError}
          </p>
        )}
      </form>

      {error && (
        <p role="alert" className="text-xs text-red-400">
          {error}
        </p>
      )}

      {loading ? (
        <div className="flex justify-center py-6">
          <Spinner size={24} />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-slate-400">
              <tr>
                <th className="py-1 pr-2">Email</th>
                <th className="py-1 pr-2">Rol</th>
                <th className="py-1 pr-2">Activo</th>
                <th className="py-1 pr-2">Creado</th>
                <th className="py-1 pr-2">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {users.map((u) => (
                <tr key={u.id} className="text-slate-200">
                  <td className="py-2 pr-2 font-mono">{u.email}</td>
                  <td className="py-2 pr-2">
                    <select
                      value={u.role}
                      onChange={(e) =>
                        void handleChangeRole(u, e.target.value as Role)
                      }
                      className="rounded bg-slate-800 px-2 py-1 text-xs ring-1 ring-slate-700"
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 pr-2">
                    <span
                      className={
                        u.isActive
                          ? "rounded bg-emerald-500/20 px-2 py-0.5 text-emerald-300"
                          : "rounded bg-slate-700 px-2 py-0.5 text-slate-400"
                      }
                    >
                      {u.isActive ? "Sí" : "No"}
                    </span>
                  </td>
                  <td className="py-2 pr-2 text-slate-500">
                    {new Date(u.createdAt).toLocaleDateString()}
                  </td>
                  <td className="py-2 pr-2">
                    <button
                      onClick={() => void handleToggleActive(u)}
                      className="rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
                    >
                      {u.isActive ? "Desactivar" : "Activar"}
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-3 text-slate-500">
                    Sin usuarios.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
